import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CustomButton } from "../../components/CustomButton";
import { Text } from "../../components/Text";
import CustomSkeleton from "../../components/CustomSkeleton";
import { getTicketChatMessages } from "../../api/supportticket.service";
import useTicketChatSocket from "../../hooks/useTicketChatSocket";
import styles from "./TicketDetail.module.css";

const PAGE_LIMIT = 20;
const MAX_MESSAGE_LENGTH = 2000;

const getMessageKey = (message) => {
  if (message?.id != null) return String(message.id);
  return [
    message?.createdAt || "",
    message?.senderType || "",
    message?.message || "",
  ].join("::");
};

const toMillis = (value) => {
  const date = new Date(value);
  const time = date.getTime();
  return Number.isNaN(time) ? 0 : time;
};

const normalizeMessage = (message) => {
  const text = String(
    message?.message ??
      message?.text ??
      message?.content ??
      message?.body ??
      "",
  );
  const rawDirection = String(message?.direction || "").toLowerCase();
  const rawSenderType = String(message?.senderType || "").toLowerCase();
  const rawSenderRole = String(message?.senderRole || "").toLowerCase();

  let direction =
    rawDirection === "outbound" || rawDirection === "inbound" ? rawDirection : "";
  let senderType =
    rawSenderType === "admin" || rawSenderType === "user" ? rawSenderType : "";

  // Legacy fallback for older payloads.
  if (!direction) {
    if (rawSenderRole === "deskala") {
      direction = "outbound";
      if (!senderType) senderType = "admin";
    } else {
      direction = "inbound";
      if (!senderType) senderType = "user";
    }
  }

  // Ensure senderType is always available.
  if (!senderType) {
    senderType = direction === "outbound" ? "admin" : "user";
  }

  return {
    ...message,
    id: message?.id ?? message?.messageId ?? message?._id,
    message: text,
    createdAt:
      message?.createdAt ||
      message?.timestamp ||
      message?.updatedAt ||
      new Date().toISOString(),
    senderName: message?.senderName || "",
    senderType,
    direction,
    isMine: direction === "outbound",
  };
};

const mergeUniqueMessages = (list) => {
  const map = new Map();
  list.forEach((item) => {
    const normalized = normalizeMessage(item);
    if (!normalized.message.trim()) return;
    map.set(getMessageKey(normalized), normalized);
  });
  return Array.from(map.values()).sort(
    (a, b) => toMillis(a.createdAt) - toMillis(b.createdAt),
  );
};

const normalizeError = (error, fallback) => {
  const payload = error?.response?.data;
  return (
    payload?.message ||
    payload?.error ||
    (typeof payload === "string" ? payload : null) ||
    error?.message ||
    fallback
  );
};

const TicketDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const ticket = location.state?.ticket;

  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState("");
  const [composerError, setComposerError] = useState("");
  const [socketError, setSocketError] = useState("");
  const [unauthorized, setUnauthorized] = useState(false);
  const [input, setInput] = useState("");

  const token = localStorage.getItem("token");
  const company = JSON.parse(localStorage.getItem("companykey") || "{}");
  const companyId = company?.id;

  const loadMessages = useCallback(
    async (targetPage = 0) => {
      if (!id) return;
      if (targetPage === 0) {
        setLoadingInitial(true);
      } else {
        setLoadingOlder(true);
      }
      setError("");
      try {
        const response = await getTicketChatMessages(
          id,
          targetPage,
          PAGE_LIMIT,
        );
        const payload = response?.data || {};
        const rows = Array.isArray(payload?.rows) ? payload.rows : [];
        const count = Number(payload?.count ?? rows.length);
        setTotalCount(Number.isFinite(count) ? count : rows.length);
        const normalizedRows = rows.map((row) => normalizeMessage(row));
        if (targetPage === 0) {
          setMessages(mergeUniqueMessages(normalizedRows));
        } else {
          setMessages((prev) =>
            mergeUniqueMessages([...normalizedRows, ...prev]),
          );
        }
        setPage(targetPage);
        setUnauthorized(false);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          setUnauthorized(true);
          setError("You are not authorized to access this ticket chat.");
          return;
        }
        setError(normalizeError(err, "Failed to load chat messages."));
      } finally {
        setLoadingInitial(false);
        setLoadingOlder(false);
      }
    },
    [id],
  );

  useEffect(() => {
    loadMessages(0);
  }, [loadMessages]);

  const onSocketMessage = useCallback((payload) => {
    setMessages((prev) =>
      mergeUniqueMessages([...prev, normalizeMessage(payload)]),
    );
  }, []);

  const onSocketError = useCallback((payload) => {
    const status = payload?.status || payload?.code;
    if (status === 401 || status === 403) {
      setUnauthorized(true);
      setSocketError("You are not authorized to use ticket chat.");
      return;
    }
    setSocketError(
      payload?.message ||
        payload?.error ||
        "Unable to send/receive messages right now.",
    );
  }, []);

  const { connected, sendMessage } = useTicketChatSocket({
    companyId,
    supportTicketId: id,
    token,
    enabled: Boolean(id && token && companyId && !unauthorized),
    onMessage: onSocketMessage,
    onError: onSocketError,
  });

  const hasMore = messages.length < totalCount;
  const inputLength = input.trim().length;

  const formattedTicketId = useMemo(() => {
    const parsed = Number.parseInt(id, 10);
    if (Number.isFinite(parsed))
      return `SKLBT ${String(parsed).padStart(3, "0")}`;
    return id;
  }, [id]);

  const handleLoadOlder = () => {
    if (loadingOlder || !hasMore) return;
    loadMessages(page + 1);
  };

  const handleSend = () => {
    const message = input.trim();
    setComposerError("");
    setSocketError("");

    if (!message) {
      setComposerError("Message cannot be empty.");
      return;
    }
    if (message.length > MAX_MESSAGE_LENGTH) {
      setComposerError(
        `Message length should be ${MAX_MESSAGE_LENGTH} characters or less.`,
      );
      return;
    }

    const sent = sendMessage(message, "text");
    if (!sent) {
      setSocketError("Unable to send message. Please reconnect and try again.");
      return;
    }
    setInput("");
  };

  return (
    <div className={styles.page}>
      <div className={styles.topRow}>
        <div className={styles.topTitleWrap}>
          <Text type="heading">Ticket Chat</Text>
        </div>
        <CustomButton
          varient="outline"
          icon="pi pi-arrow-left"
          label="Back"
          onClick={() => navigate("/support")}
        />
      </div>

      <div className={styles.chatShell}>
        <div className={styles.ticketMetaStrip}>
          <div className={styles.ticketMetaLeft}>
            <div className={styles.ticketMetaLabel}>Ticket</div>
            <div className={styles.ticketIdentity}>#{formattedTicketId}</div>
            <div className={styles.ticketSubject}>
              {ticket?.subject || "Support conversation"}
            </div>
          </div>
        </div>

        {unauthorized && (
          <div className={styles.stateError}>
            Unauthorized: chat access denied.
          </div>
        )}
        {!unauthorized && (
          <>
            <div className={styles.messages}>
              {loadingInitial && (
                <div className={styles.stateBlock}>
                  <CustomSkeleton
                    parentcss="flex flex-column gap-2"
                    countline={5}
                    height="20px"
                  />
                </div>
              )}

              {!loadingInitial && hasMore && (
                <div className={styles.olderWrap}>
                  <CustomButton
                    varient="outline"
                    label={loadingOlder ? "Loading..." : "Load older messages"}
                    onClick={handleLoadOlder}
                    disabled={loadingOlder}
                  />
                </div>
              )}

              {!loadingInitial && messages.length === 0 && !error && (
                <div className={styles.stateEmpty}>No messages yet.</div>
              )}

              {!loadingInitial &&
                messages.map((item) => (
                  <div
                    key={getMessageKey(item)}
                    className={`${styles.messageRow} ${
                      item.direction === "outbound"
                        ? styles.outboundRow
                        : styles.inboundRow
                    }`}
                  >
                    <div
                      className={`${styles.bubble} ${
                        item.direction === "outbound"
                          ? styles.adminBubble
                          : styles.userBubble
                      }`}
                    >
                      <div>{item.message}</div>
                      <div className={styles.messageMeta}>
                        <span>{item.senderType === "admin" ? "Admin" : "User"}</span>
                        <span> • </span>
                        <span>{new Date(item.createdAt).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {(error || socketError) && (
              <div className={styles.stateError}>{error || socketError}</div>
            )}

            <div className={styles.composer}>
              <div className={styles.composerInner}>
                <div className={styles.inputWrap}>
                  <textarea
                    className={styles.textarea}
                    value={input}
                    maxLength={MAX_MESSAGE_LENGTH}
                    placeholder="Write your message..."
                    onChange={(e) => {
                      setComposerError("");
                      setInput(e.target.value);
                    }}
                  />
                  <div className={styles.helperText}>
                    {inputLength}/{MAX_MESSAGE_LENGTH}
                  </div>
                  <CustomButton
                    varient="filled"
                    icon="pi pi-send"
                    label="Send"
                    onClick={handleSend}
                    disabled={!connected}
                    style={{
                      position: "absolute",
                      right: "8px",
                      bottom: "8px",
                      minWidth: "92px",
                      height: "30px",
                    }}
                  />
                </div>
              </div>
              {composerError && (
                <div className={styles.stateError}>{composerError}</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TicketDetail;
